import { Injectable, signal } from '@angular/core';

export type CookieConsentChoice = 'accepted' | 'rejected';

interface CookieConsentState {
  readonly choice: CookieConsentChoice;
  readonly analyticsEnabled: boolean;
  readonly timestampUtc: string;
}

const STORAGE_KEY = 'mkzs-cookie-consent-v1';

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  readonly consent = signal<CookieConsentState | null>(this.readStoredConsent());

  readonly shouldShowBanner = signal(this.consent() === null);

  acceptAnalytics(): void {
    const state: CookieConsentState = {
      choice: 'accepted',
      analyticsEnabled: true,
      timestampUtc: new Date().toISOString(),
    };

    this.persist(state);
  }

  rejectOptionalCookies(): void {
    const state: CookieConsentState = {
      choice: 'rejected',
      analyticsEnabled: false,
      timestampUtc: new Date().toISOString(),
    };

    this.persist(state);
  }

  private persist(state: CookieConsentState): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    this.consent.set(state);
    this.shouldShowBanner.set(false);
  }

  private readStoredConsent(): CookieConsentState | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as CookieConsentState;
      if (!parsed?.choice || typeof parsed.analyticsEnabled !== 'boolean') {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }
}
